import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { TempChatProvider } from '@/contexts/TempChatContext';
import { AuthGuard } from '@/components/AuthGuard';
import { AuthProvider } from '@/contexts/AuthContext';
import { useBackgroundToggleHotkey } from '@/hooks/useBackgroundToggleHotkey';
import { useEffect } from 'react';
import { initNewsNotifications } from '@/services/pushNotifications';
import Index from '@/pages/Index';
import ChitraLayout from '@/pages/chitra/ChitraLayout';
import ChitraHome from '@/pages/chitra/ChitraHome';
import ChitraLibrary from '@/pages/chitra/ChitraLibrary';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import NotFound from '@/pages/NotFound';
import AiNews from '@/pages/ainews';
import BookmarkedArticles from '@/pages/BookmarkedArticles';
import Settings from '@/pages/Settings';
import Aawaz from '@/pages/Aawaz';
import Auth from '@/pages/Auth';
import SudokuPage from '@/pages/SudokuPage';
import Discover from '@/pages/Discover';
import DiscoverDetail from '@/pages/DiscoverDetail';
import DocView from '@/pages/DocView';

import Shared from '@/pages/Shared';
import SlidesViewer from '@/pages/SlidesViewer';
import { PricingPage } from '@/pages/PricingPage';
import { ThemeDynamicColors } from '@/components/ThemeDynamicColors';
import Run from '@/pages/Run';
import OG from '@/pages/OG';

const queryClient = new QueryClient();

function App() {
  useBackgroundToggleHotkey();

  useEffect(() => {
    // Simple context menu prevention for images only
    const preventContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    // Prevent image drag
    const preventImageDrag = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventImageDrag);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventImageDrag);
    };
  }, []);

  useEffect(() => {
    // Initialize push notifications (no DB storage). Safe-guards for protocol/permission are inside.
    initNewsNotifications().catch(() => { });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TempChatProvider>
        <Toaster />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <ThemeDynamicColors />
            <Routes>

              <Route path="/auth" element={<Auth />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/s/:shareId" element={<Shared />} />
              <Route path="/" element={<AuthGuard><Index /></AuthGuard>} />
              <Route path="/incognito" element={<AuthGuard><Index /></AuthGuard>} />
              <Route path="/og" element={<AuthGuard><OG /></AuthGuard>} />
              <Route path="/c/:chatId" element={<AuthGuard><Index /></AuthGuard>} />
              <Route path="/c/:chatId" element={<AuthGuard><Index /></AuthGuard>} />
              <Route path="/chitra" element={<AuthGuard><ChitraLayout /></AuthGuard>}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<ChitraHome />} />
                <Route path="library" element={<ChitraLibrary />} />
              </Route>

              <Route path="/aawaz" element={<AuthGuard><Aawaz /></AuthGuard>} />
              <Route path="/bookmark" element={<AuthGuard><BookmarkedArticles /></AuthGuard>} />
              <Route path="/settings">
                <Route index element={<AuthGuard><Settings /></AuthGuard>} />
                <Route path=":tabId" element={<AuthGuard><Settings /></AuthGuard>} />
              </Route>
              <Route path="/ai-news" element={<AuthGuard><AiNews /></AuthGuard>} />
              <Route path="/upgrade" element={<AuthGuard><PricingPage /></AuthGuard>} />
              <Route path="/sudoku" element={<AuthGuard><SudokuPage /></AuthGuard>} />
              <Route path="/discover" element={<AuthGuard><Discover /></AuthGuard>} />
              <Route path="/discover/:type/:id" element={<AuthGuard><DiscoverDetail /></AuthGuard>} />
              <Route path="/doc" element={<AuthGuard><DocView /></AuthGuard>} />

              <Route path="/slides/:id" element={<AuthGuard><SlidesViewer /></AuthGuard>} />
              <Route path="/run/:runId" element={<Run />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TempChatProvider>
    </QueryClientProvider>
  );
}

export default App;
